import { AccountDescription } from "portal/components/users/AccountDescription";
import { buildPermission } from "portal/utils/auth";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { capitalize } from "portal/utils/strings";
import { CarbonCard } from "portal/components/CarbonCard";
import {
  DEFAULT_LANG,
  isPortalLanguage,
  LANGUAGES,
  portalLanguageOrDefault,
} from "portal/i18nConstants";
import { DeleteAccount } from "portal/components/users/DeleteAccount";
import { Header } from "portal/components/header/Header";
import { isUndefined } from "portal/utils/identity";
import { Loading } from "portal/components/Loading";
import { MeasurementSystem } from "portal/utils/units/units";
import { Page } from "portal/components/Page";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
  UserDisplayRole,
  userDisplayRoleToJSON,
} from "protos/portal/auth";
import { portalReadyApi } from "common/state/portalApi/ready";
import { setLocale, toDisplayName } from "common/utils/i18n";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import { useAuth0 } from "@auth0/auth0-react";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router-dom";
import { userApi } from "../../../common/src/state/portalApi/user";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { withAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import Grid from "@mui/material/Grid";
import React, { FunctionComponent, useEffect, useState } from "react";

const _Settings: FunctionComponent = () => {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, measurementSystem, isLoading } = useSelf();
  const readyResponse = useQueryPopups(
    portalReadyApi.endpoints.ready.useQuery()
  );

  const [updateUser] = useMutationPopups(
    userApi.endpoints.updateUser.useMutation(),
    {
      success: capitalize(
        t("utils.actions.savedLong", {
          subject: t("models.users.user_one"),
        })
      ),
    }
  );

  const [unit, setUnit] = useState<MeasurementSystem>(
    MeasurementSystem.imperial
  );

  useEffect(() => {
    setUnit(measurementSystem);
  }, [measurementSystem]);

  if (!user) {
    return <Loading failed={!isLoading} />;
  }

  return (
    <>
      <Header title={t("views.settings.title")}>
        <div className="w-full flex items-center justify-end">
          <Button
            variant="text"
            color="inherit"
            onClick={() =>
              logout({ logoutParams: { returnTo: window.location.origin } })
            }
          >
            {t("views.settings.logOut")}
          </Button>
        </div>
      </Header>
      <Page>
        <div className="flex flex-col">
          <Grid container className="w-full items-stretch" spacing={2}>
            <CarbonCard
              title={t("views.settings.cards.localization")}
              error={false}
              success={true}
              content={
                <>
                  <FormControl>
                    <InputLabel>
                      {t("views.settings.fields.language")}
                    </InputLabel>
                    <Select<string>
                      input={
                        <OutlinedInput
                          label={t("views.settings.fields.language")}
                        />
                      }
                      defaultValue={DEFAULT_LANG}
                      value={i18n.language}
                      onChange={(event) => {
                        const locale = portalLanguageOrDefault(
                          event.target.value
                        );
                        setLocale(i18n, locale);
                        if (user.userId) {
                          updateUser({
                            userId: user.userId,
                            user: { userMetadata: { language: locale } },
                          });
                        }
                      }}
                      renderValue={(value) =>
                        isPortalLanguage(value) ? toDisplayName(value) : ""
                      }
                    >
                      {LANGUAGES.map((language) => (
                        <MenuItem value={language} key={language}>
                          {toDisplayName(language)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      {t("views.settings.fields.measurement.name")}
                    </FormLabel>
                    <RadioGroup
                      row
                      name="measurement-system"
                      value={unit}
                      onChange={(event) => {
                        const value = event.target.value as MeasurementSystem;
                        setUnit(value);
                        if (!user.email || !user.userId) {
                          return;
                        }
                        updateUser({
                          userId: user.userId,
                          user: { userMetadata: { unit: value } },
                        });
                      }}
                    >
                      {[
                        MeasurementSystem.imperial,
                        MeasurementSystem.metric,
                      ].map((unitType) => (
                        <FormControlLabel
                          key={unitType}
                          value={unitType}
                          control={<Radio />}
                          // carbon.actions.compareKeys.ignoreDynamic
                          label={t(
                            `views.settings.fields.measurement.values.${unitType}`
                          )}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </>
              }
            />
            <CarbonCard
              title={t("views.settings.cards.account")}
              error={false}
              success={!isUndefined(user)}
              content={
                <>
                  <AccountDescription user={user} />
                  <Typography>
                    <label>{t("models.users.role_one")}: </label>
                    <strong>
                      {t(
                        `models.users.roles.${userDisplayRoleToJSON(
                          user.appMetadata?.role ?? UserDisplayRole.UNRECOGNIZED
                        )}`
                      )}
                    </strong>
                  </Typography>
                </>
              }
              actions={
                <DeleteAccount
                  user={user}
                  onDelete={() => {
                    navigate(Path.LOGOUT);
                  }}
                />
              }
            />
            <Grid size={{ xs: 12, lg: 6 }}>
              <div className="block font-mono text-xs text-right ">
                <div>
                  {t("views.settings.version", {
                    version: window._jsenv.REACT_APP_RELEASE,
                    hash: window._jsenv.REACT_APP_COMMITHASH.slice(0, 8),
                  })}
                </div>
                {readyResponse.data && (
                  <>
                    <div>
                      {t("views.settings.backendVersion", {
                        version: readyResponse.data.version,
                        hash: readyResponse.data.commit.slice(0, 8),
                      })}
                    </div>
                    <div>
                      {t("views.settings.jigsawMinSoftwareVersion", {
                        version: readyResponse.data.jigsawMinSoftwareVersion,
                      })}
                    </div>
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </Page>
    </>
  );
};

export const Settings = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.update,
        PermissionResource.users,
        PermissionDomain.self
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.users,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.users,
        PermissionDomain.all
      ),
    ],
  },
  _Settings,
  <UnauthorizedPage />
);
